<script setup lang="ts">
import type { Component } from 'vue';

import Logo from '../../../static/images/logo.svg';

defineProps<{
    brandUrl?: string;
    icon?: Component;
    product?: {
        name: string;
        description: string | null;
        color: string;
        iconHtml: string | null;
    };
    description?: string | null;
    hideIntroOnMobile?: boolean;
    title?: string;
}>();
</script>

<template>
    <div
        class="public-page grow section container gap-gutter"
        :class="{ 'public-page--intro-only': !$slots.default && !$slots.footer }"
    >
        <header class="public-page__header stack gap-xl wrap-balance">
            <a
                v-if="brandUrl"
                :href="brandUrl"
                class="public-page__logo logo text-xl align-self-start"
            >
                <Logo />
            </a>

            <slot name="branding" />

            <div
                v-if="icon || title || description || $slots.title || $slots.description"
                class="stack gap-md"
                :class="{ 'public-page__mobile-hidden': hideIntroOnMobile }"
            >
                <component
                    :is="icon"
                    v-if="icon"
                    class="public-page__icon icon text-xxl color-muted"
                />

                <h1 v-if="title || $slots.title" class="h2">
                    <slot name="title">{{ title }}</slot>
                </h1>

                <div
                    v-if="description || $slots.description"
                    class="public-page__description stack gap-md color-muted"
                >
                    <slot name="description">
                        <p>{{ description }}</p>
                    </slot>
                </div>
            </div>

            <div v-if="product" class="public-page__product stack gap-sm">
                <div class="h2 row gap-sm" :style="{ color: product.color }">
                    <span v-html="product.iconHtml" class="icon text-xxl" />
                    {{ product.name }}
                </div>
                <p class="content">{{ product.description }}</p>
            </div>
        </header>

        <main
            v-if="$slots.default || $slots.footer"
            class="public-page__card card p-xl stack gap-xl justify-center"
        >
            <slot />

            <div v-if="$slots.footer" class="stack gap-xs align-center mt-auto">
                <slot name="footer" />
            </div>
        </main>
    </div>
</template>

<style scoped lang="less">
.public-page {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 45ch));
    justify-content: center;
    align-items: start;
}

.public-page--intro-only {
    grid-template-columns: minmax(0, 45ch);
}

.public-page__description {
    font-size: var(--text-md);
    line-height: var(--line-height-expanded);
    overflow-wrap: break-word;
}

.public-page__header h1 {
    font-size: 2.5rem;
}

.public-page__icon {
    align-self: start;
}

.public-page__product :deep(.icon > img),
.public-page__product :deep(.icon > svg) {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

@media @query-tablet-down {
    .public-page {
        align-content: start;
        grid-template-columns: minmax(0, 45ch);
    }

    .public-page__header {
        align-items: center;
        text-align: center;
        padding-block: var(--space-xl);
    }

    .public-page__logo {
        align-self: center;
    }

    .public-page__icon {
        align-self: center;
    }

    .public-page__mobile-hidden {
        display: none;
    }

    .public-page__product {
        align-items: center;
    }

    .public-page__description {
        font-size: var(--text-sm);
        line-height: var(--line-height-default);
    }

    .public-page__header h1 {
        font-size: var(--text-xl);
    }
}
</style>
